import { type AxiosError } from 'axios'
import {
  type IdentityLoginEmailRequest,
  type IdentitySendSmsRequestType,
  type IdentityLoginPhoneRequest,
  type IdentityRegisterPhoneRequest,
} from '@/types/origin-point-engine'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { getCookie, setCookie, removeCookie } from '@/lib/cookies'
import { logger } from '@/lib/logger'
import { showSuccessData, showErrorData } from '@/lib/toast'
import {
  type SendCodeMutate,
  type VerifyCodeMutate,
  type LoginMutate,
  type LogoutMutate,
  type ChangePasswordMutate,
  type SendSmsMutate,
  type LoginPhoneMutate,
  type RegisterPhoneMutate,
  type BindPhoneMutate,
} from '@/features/auth/types/mutate'
import { type User as AuthUser } from '../index'
import { type VerifyCodeResponse, type LoginResponse } from '../types/type'

const ACCESS_TOKEN = 'access-token-key'

interface AuthState {
  // --- Slice 1: Auth State & Atomic Actions ---
  auth: {
    user: AuthUser | null
    accessToken: string
    setUser: (user: AuthUser | null) => void
    setAccessToken: (token: string) => void
    reset: () => void
  }

  // --- Slice 2: Dialog State & Atomic Actions ---
  authDialog: {
    open: boolean
    setOpen: (open: boolean) => void
  }

  changePasswordDialog: {
    open: boolean
    setOpen: (open: boolean) => void
  }

  changePhoneDialog: {
    open: boolean
    setOpen: (open: boolean) => void
  }

  // --- Slice 3: View State & Atomic Actions ---
  authView: {
    view: 'login' | 'register'
    loginMode: 'email-code' | 'email-password' | 'phone-code'
    setView: (view: 'login' | 'register') => void
    setLoginMode: (mode: 'email-code' | 'email-password' | 'phone-code') => void
  }

  // --- Business Intent Handlers ---
  handlers: {
    handleSetAccessToken: (token: string) => void
    handleLogout: (
      mutateAsync: LogoutMutate,
      onSuccess?: () => void
    ) => Promise<void>
    handleSendCode: (
      email: string,
      mutateAsync: SendCodeMutate,
      onSuccess?: () => void
    ) => Promise<void>
    handleSendSms: (
      phone: string,
      type: IdentitySendSmsRequestType,
      mutateAsync: SendSmsMutate,
      onSuccess?: () => void
    ) => Promise<void>
    handleVerifyCode: (
      email: string,
      code: string,
      mutateAsync: VerifyCodeMutate
    ) => Promise<void>
    handleLogin: (
      data: IdentityLoginEmailRequest,
      mutateAsync: LoginMutate
    ) => Promise<void>
    handlePhoneLogin: (
      data: IdentityLoginPhoneRequest,
      mutateAsync: LoginPhoneMutate
    ) => Promise<void>
    handlePhoneRegister: (
      data: IdentityRegisterPhoneRequest,
      mutateAsync: RegisterPhoneMutate
    ) => Promise<void>
    handleChangePassword: (
      email: string,
      code: string,
      newPassword: string,
      verifyAsync: VerifyCodeMutate,
      changeAsync: ChangePasswordMutate
    ) => Promise<void>
    handleBindPhone: (
      phone: string,
      code: string,
      mutateAsync: BindPhoneMutate
    ) => Promise<void>
  }
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => {
      const cookieState = getCookie(ACCESS_TOKEN)
      const initToken = cookieState ? JSON.parse(cookieState) : ''
      logger.debug('AuthStore init token:', initToken)

      return {
        // --- Auth Slice ---
        auth: {
          user: null,
          accessToken: initToken,
          setUser: (user) =>
            set((s) => ({ auth: { ...s.auth, user } }), false, 'auth/setUser'),
          setAccessToken: (accessToken) =>
            set(
              (s) => ({ auth: { ...s.auth, accessToken } }),
              false,
              'auth/setAccessToken'
            ),
          reset: () => {
            removeCookie(ACCESS_TOKEN)
            set(
              (s) => ({ auth: { ...s.auth, user: null, accessToken: '' } }),
              false,
              'auth/reset'
            )
          },
        },

        // --- Dialog Slice ---
        authDialog: {
          open: false,
          setOpen: (open) =>
            set(
              (s) => ({ authDialog: { ...s.authDialog, open } }),
              false,
              'authDialog/setOpen'
            ),
        },

        // --- Change Password Dialog Slice ---
        changePasswordDialog: {
          open: false,
          setOpen: (open) =>
            set(
              (s) => ({
                changePasswordDialog: { ...s.changePasswordDialog, open },
              }),
              false,
              'changePasswordDialog/setOpen'
            ),
        },

        // --- Change Phone Dialog Slice ---
        changePhoneDialog: {
          open: false,
          setOpen: (open) =>
            set(
              (s) => ({
                changePhoneDialog: { ...s.changePhoneDialog, open },
              }),
              false,
              'changePhoneDialog/setOpen'
            ),
        },

        // --- View Slice ---
        authView: {
          view: 'login',
          loginMode: 'phone-code',
          setView: (view) =>
            set(
              (s) => ({ authView: { ...s.authView, view } }),
              false,
              'authView/setView'
            ),
          setLoginMode: (loginMode) =>
            set(
              (s) => ({ authView: { ...s.authView, loginMode } }),
              false,
              'authView/setLoginMode'
            ),
        },

        // --- Handlers ---
        handlers: {
          handleSetAccessToken: (token) => {
            setCookie(ACCESS_TOKEN, JSON.stringify(token))
            get().auth.setAccessToken(token)
          },

          handleLogout: async (mutateAsync, onSuccess) => {
            try {
              await mutateAsync()
              get().auth.reset()
              showSuccessData('已退出登录')
              onSuccess?.()
            } catch (error) {
              logger.error('Logout failed:', error)
              get().auth.reset()
              onSuccess?.()
            }
          },

          handleSendCode: async (email, mutateAsync, onSuccess) => {
            try {
              await mutateAsync({ data: { email } })
              onSuccess?.()
            } catch (error) {
              const errorResponse = error as AxiosError
              if (errorResponse.response?.status === 400) {
                showErrorData('邮箱格式有误，请重新输入')
              } else {
                showErrorData('验证码发送失败，请重试')
              }
              throw error
            }
          },

          handleSendSms: async (phone, type, mutateAsync, onSuccess) => {
            try {
              await mutateAsync({ data: { phone, type } })
              onSuccess?.()
            } catch (error) {
              const errorResponse = error as AxiosError
              if (errorResponse.response?.status === 400) {
                showErrorData('手机号格式有误，请重新输入')
              } else {
                showErrorData('短信发送失败，请重试')
              }
              throw error
            }
          },

          handleVerifyCode: async (email, code, mutateAsync) => {
            try {
              const data = await mutateAsync({
                data: { email, code },
              })
              const response = data as unknown as VerifyCodeResponse
              const token = response.data.accessToken
              const user = response.data.user

              setCookie(ACCESS_TOKEN, JSON.stringify(token))
              get().auth.setAccessToken(token)
              get().auth.setUser(user)

              showSuccessData('验证成功')
              get().authDialog.setOpen(false)
            } catch (error) {
              const axiosError = error as AxiosError
              if (axiosError.response?.status === 400) {
                showErrorData('验证码错误，请重新输入')
              } else {
                showErrorData('登录失败，请重试')
              }
              throw error
            }
          },

          handleLogin: async (formData, mutateAsync) => {
            try {
              const data = await mutateAsync({
                data: formData,
              })
              const response = data as unknown as LoginResponse
              const token = response.data.accessToken
              const user = response.data.user

              setCookie(ACCESS_TOKEN, JSON.stringify(token))
              get().auth.setAccessToken(token)
              get().auth.setUser(user)

              showSuccessData('登录成功，祝您游玩愉快')
              get().authDialog.setOpen(false)
            } catch (error) {
              const axiosError = error as AxiosError
              if (axiosError.response?.status === 401) {
                showErrorData('邮箱或密码错误')
              } else {
                showErrorData('登录失败，请重试')
              }
              throw error
            }
          },

          handlePhoneLogin: async (formData, mutateAsync) => {
            try {
              const data = await mutateAsync({
                data: formData,
              })
              // 使用 Orval 生成的类型
              const response = data //as PostAuthLoginPhone200
              const token = response.data?.accessToken
              const user = response.data?.user

              if (token) {
                setCookie(ACCESS_TOKEN, JSON.stringify(token))
                get().auth.setAccessToken(token)
                get().auth.setUser(user as AuthUser) // Cast if needed, or ensure AuthUser matches DTO

                showSuccessData('登录成功')
                get().authDialog.setOpen(false)
              }
            } catch (error) {
              const axiosError = error as AxiosError
              if (axiosError.response?.status === 400) {
                showErrorData('验证码错误')
              } else if (axiosError.response?.status === 404) {
                showErrorData('用户不存在，请先注册')
              } else {
                showErrorData('登录失败，请重试')
              }
              throw error
            }
          },

          handlePhoneRegister: async (formData, mutateAsync) => {
            try {
              const data = await mutateAsync({
                data: formData,
              })
              const response = data //as PostAuthRegisterPhone200
              const token = response.data?.accessToken
              const user = response.data?.user

              if (token) {
                setCookie(ACCESS_TOKEN, JSON.stringify(token))
                get().auth.setAccessToken(token)
                get().auth.setUser(user as AuthUser)

                showSuccessData('注册成功')
                get().authDialog.setOpen(false)
              }
            } catch (error) {
              const axiosError = error as AxiosError
              if (axiosError.response?.status === 400) {
                showErrorData('注册信息有误')
              } else if (axiosError.response?.status === 409) {
                showErrorData('用户已存在')
              } else {
                showErrorData('注册失败，请重试')
              }
              throw error
            }
          },

          handleChangePassword: async (
            email,
            code,
            newPassword,
            verifyAsync,
            changeAsync
          ) => {
            try {
              // 1. 调用验证码验证接口
              const verifyRes = await verifyAsync({
                data: { email, code },
              })

              const response = verifyRes as unknown as VerifyCodeResponse
              const token = response.data.accessToken
              const user = response.data.user

              if (!token) {
                showErrorData('验证失败，请重新获取验证码')
                return
              }

              // 2. 更新当前会话状态（为了后续修改密码接口能带上正确的 Token）
              setCookie(ACCESS_TOKEN, JSON.stringify(token))
              get().auth.setAccessToken(token)
              get().auth.setUser(user)

              // 3. 调用修改密码接口
              await changeAsync({
                data: { newPassword },
              })

              showSuccessData('密码修改成功')
              get().changePasswordDialog.setOpen(false)
            } catch (err) {
              logger.error('Change password failed:', err)
              const axiosError = err as AxiosError

              // 优化错误反馈逻辑
              if (axiosError.response?.status === 400) {
                showErrorData('验证码错误或密码格式不正确')
              } else if (axiosError.response?.status === 401) {
                showErrorData('验证会话已过期，请重新发送验证码')
              } else {
                showErrorData('修改密码失败，请稍后重试')
              }
              throw err
            }
          },

          handleBindPhone: async (phone, code, mutateAsync) => {
            try {
              await mutateAsync({
                data: { phone, code },
              })
              get().changePhoneDialog.setOpen(false)
            } catch (error) {
              const axiosError = error as AxiosError
              if (axiosError.response?.status === 400) {
                showErrorData('验证码错误')
              } else if (axiosError.response?.status === 409) {
                showErrorData('该手机号已被绑定')
              } else {
                showErrorData('绑定失败，请重试')
              }
              throw error
            }
          },
        },
      }
    },
    { name: 'AuthStore' }
  )
)
